import { useMemo, useRef, type ReactNode, type KeyboardEvent, type ClipboardEvent } from 'react';
import { onlyDigits } from '@/shared/utils/phone';

/** Caixa "Confirmo os termos" do layout (checkbox nativo + link que abre o TermsDialog). */
export function TermsCheck({ checked, onChange, onOpenTerms }: { checked: boolean; onChange: (v: boolean) => void; onOpenTerms: () => void }) {
  return (
    <label className="terms">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <strong>Confirmo os termos</strong>
        <small>
          Li e aceito os{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenTerms(); }}>termos de uso</a>
          {' '}e política de privacidade.
        </small>
      </span>
    </label>
  );
}

/** Modal ornamentado do reCAPTCHA (fecha clicando fora, exceto enquanto envia). */
export function CaptchaModal({ loading, text, onClose, children }: { loading: boolean; text: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="lb-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="lb-modal ornate-card" onClick={(e) => e.stopPropagation()}>
        <h3>Confirme que você é humano</h3>
        <p>{text}</p>
        <div className="recaptcha-slot">{children}</div>
        <button type="button" className="btn btn-dark submit-btn" onClick={onClose} disabled={loading}>Cancelar</button>
      </div>
    </div>
  );
}

/** Codigo de 6 digitos (SMS) — cola, backspace e setas como antes. */
export function OtpInput({ length = 6, value, onChange, disabled }: { length?: number; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const vals = useMemo(() => {
    const clean = onlyDigits(value).slice(0, length);
    return Array.from({ length }, (_, i) => clean[i] ?? '');
  }, [value, length]);
  const focusAt = (i: number) => { refs.current[i]?.focus(); refs.current[i]?.select(); };
  const setChar = (i: number, char: string) => {
    const clean = onlyDigits(char).slice(-1);
    const arr = [...vals]; arr[i] = clean; onChange(arr.join(''));
    if (clean && i < length - 1) focusAt(i + 1);
  };
  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (vals[i]) setChar(i, '');
      else if (i > 0) { focusAt(i - 1); const arr = [...vals]; arr[i - 1] = ''; onChange(arr.join('')); }
    }
    if (e.key === 'ArrowLeft' && i > 0) focusAt(i - 1);
    if (e.key === 'ArrowRight' && i < length - 1) focusAt(i + 1);
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = onlyDigits(e.clipboardData.getData('text')).slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    requestAnimationFrame(() => focusAt(Math.min(text.length - 1, length - 1)));
  };
  return (
    <div className="otp-row">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={vals[i]}
          disabled={disabled}
          onPaste={onPaste}
          onKeyDown={(e) => onKeyDown(i, e)}
          onChange={(e) => setChar(i, e.target.value)}
        />
      ))}
    </div>
  );
}
